/**
 * 100ViewsOnly — Supporter Milestone Service
 * Checks and awards milestone badges when supporter count increases
 */

import {
  doc, getDoc, updateDoc, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";

export const SUPPORTER_MILESTONES = [
  { count: 10,  id: "supporters_10",  label: "10 Supporters",  emoji: "🌱", color: "#22c55e", gradient: "linear-gradient(135deg,#22c55e,#15803d)", description: "First 10 supporters — the community begins" },
  { count: 25,  id: "supporters_25",  label: "25 Supporters",  emoji: "⚡", color: "#f59e0b", gradient: "linear-gradient(135deg,#f59e0b,#d97706)", description: "25 believers — serious momentum" },
  { count: 50,  id: "supporters_50",  label: "50 Supporters",  emoji: "🔥", color: "#f97316", gradient: "linear-gradient(135deg,#f97316,#ea580c)", description: "50 supporters — real traction" },
  { count: 100, id: "supporters_100", label: "100 Supporters", emoji: "💎", color: "#8b5cf6", gradient: "linear-gradient(135deg,#8b5cf6,#6d28d9)", description: "100 strong — you're building something real" },
  { count: 250, id: "supporters_250", label: "250 Supporters", emoji: "👑", color: "#ec4899", gradient: "linear-gradient(135deg,#ec4899,#be185d)", description: "250 supporters — elite creator status" },
  { count: 500, id: "supporters_500", label: "500 Supporters", emoji: "🚀", color: "#7c3aed", gradient: "linear-gradient(135deg,#7c3aed,#8b5cf6,#ec4899)", description: "500 supporters — legendary" },
];

/**
 * Get all milestones a creator has earned based on supporter count
 */
export function getEarnedMilestones(supporterCount) {
  return SUPPORTER_MILESTONES.filter(m => supporterCount >= m.count);
}

/**
 * Get the next milestone a creator is working toward
 */
export function getNextMilestone(supporterCount) {
  return SUPPORTER_MILESTONES.find(m => supporterCount < m.count) || null;
}

/**
 * Get progress percentage toward next milestone
 */
export function getMilestoneProgress(supporterCount) {
  const next = getNextMilestone(supporterCount);
  if (!next) return 100;
  const prev = SUPPORTER_MILESTONES.filter(m => supporterCount >= m.count).slice(-1)[0];
  const base = prev?.count || 0;
  return Math.min(100, Math.round(((supporterCount - base) / (next.count - base)) * 100));
}

/**
 * Check for newly earned milestones and save them to the user doc
 * Returns array of newly earned milestones (for celebration UI)
 */
export async function checkAndAwardMilestones(userId, supporterCount) {
  if (!userId) return [];

  try {
    const userRef = doc(db, "users", userId);
    const snap = await getDoc(userRef);
    if (!snap.exists()) return [];

    const data = snap.data();
    const existingMilestones = data.earnedMilestones || [];
    const earned = getEarnedMilestones(supporterCount);
    const newlyEarned = earned.filter(m => !existingMilestones.includes(m.id));

    if (newlyEarned.length === 0) return [];

    // Save new milestones to user doc
    await updateDoc(userRef, {
      earnedMilestones: [...existingMilestones, ...newlyEarned.map(m => m.id)],
      updatedAt: serverTimestamp()
    });

    return newlyEarned;
  } catch (err) {
    console.error("Milestone check error:", err);
    return [];
  }
}// ALL_MILESTONES — referenced by Profile.js
export const ALL_MILESTONES = SUPPORTER_MILESTONES.reduce((acc, m) => {
  acc[m.id] = { ...m, category: "supporters", secret: false };
  return acc;
}, {});

// checkFirstViewerBadge — referenced by socialService.js
export async function checkFirstViewerBadge(viewerId, momentId, viewCount) {
  if (!viewerId || viewCount > 10) return false;
  try {
    const userRef = doc(db, "users", viewerId);
    const snap = await getDoc(userRef);
    if (!snap.exists()) return false;
    const existing = snap.data().earnedMilestones || [];
    if (existing.includes("first_viewer")) return false;
    await updateDoc(userRef, {
      earnedMilestones: [...existing, "first_viewer"],
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (err) {
    console.error("First viewer badge error:", err);
    return false;
  }
}

// checkEarlyFanBadge — referenced by socialService.js
export async function checkEarlyFanBadge(followerId, creatorFollowerCount) {
  if (!followerId || creatorFollowerCount >= 100) return false;
  try {
    const userRef = doc(db, "users", followerId);
    const snap = await getDoc(userRef);
    if (!snap.exists()) return false;
    const existing = snap.data().earnedMilestones || [];
    if (existing.includes("early_fan")) return false;
    await updateDoc(userRef, {
      earnedMilestones: [...existing, "early_fan"],
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (err) {
    console.error("Early fan badge error:", err);
    return false;
  }
}
